import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    Select,
    DatePicker,
    Button,
    Row,
    Col,
    Typography,
    Table,
    message,
    Space,
    Divider,
    Alert,
    Spin,
    Modal
} from 'antd';
import {
    SaveOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney } from '../../utils/currency';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const { Option } = Select;

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const BankaFaturaTalepIslem = () => {
    const { id } = useParams();
    const { darkMode } = useTheme();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [contractData, setContractData] = useState(null);
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const [inflationData, setInflationData] = useState(null);
    const [error, setError] = useState(null);
    const [bankCode, setBankCode] = useState('');
    const [discountOptions, setDiscountOptions] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });
    const [formExtraData, setFormExtraData] = useState({});  

    const companyOptions = [
        { value: 1, label: 'ATM Yönetim' },
        { value: 2, label: 'MASEF' },
        { value: 3, label: 'ATM İstanbul' },
        { value: 10, label: 'ATM Kiralama' },
        { value: 11, label: 'ATM Yönetim' },
        { value: 17, label: 'Neta' }
    ];

    const invoiceTypeOptions = [
        { value: 2, label: 'Yeni Kira' },
        { value: 3, label: 'Yenileme Kira' },
        { value: 4, label: 'Söz. Yenileme' },
        { value: 5, label: 'Kabin' },
        { value: 8, label: 'Elektrik' },
        { value: 10, label: 'Temizlik' },
        { value: 11, label: 'Ortak Gider' }
    ];

    const locationStatusOptions = {
        0: 'Pasif / Silinmiş',
        1: 'Pasif / Aday',
        2: 'Kurulum Bekliyor',
        3: 'Aktif',
        4: 'Pasif / Kaldırılmış',
        5: 'Pasif / Deviredilmiş',
        6: 'Pasif / Keşif',
        99: 'Offsite'
    };

    const contractSignatureStatusOptions = {
        1: 'İmzalandı',
        2: 'Onaylandı',
        6: 'Geçici Onay'
    };

    // 26.10 düzeltmesi: etiketler hesapla aynı kodlarla (contracts.py resolve_rate, sozlesme/tabs.jsx
    // ARTIS_SEKLI): şekil 2 = önceki yıl aynı ay, 3 = 12 aylık ortalama. Önceden 1/2/3 kaymış gösteriliyordu.
    const increaseRateOptions = {
        0: 'Artış Yok',
        1: 'ÜFE',
        2: 'TÜFE',
        3: 'ÜFE + TÜFE Ortalaması',
        4: 'İTO TEFE',
        5: 'Sabit Oran'
    };

    const increaseMethodOptions = {
        0: 'Yok',
        1: 'Yıllık Ortalama',
        2: 'Bir Önceki Yılın Aynı Ayına Göre',
        3: '12 Aylık Ortalamaya Göre'
    };

    const bazKaynagiLabel = (k) => ({
        onceki_fatura: 'Önceki fatura',
        onceki_fatura_lokasyon: "Önceki fatura (lokasyon, bsk'siz)",
        kiraBedeli: 'Sözleşme kira bedeli',
        yok: 'Bulunamadı'
    }[k] || (/_kira$/.test(String(k || '')) ? `bsk ${String(k).replace('_kira', '')} kira kaydı` : (k || '-')));

    const cakismaListesi = (list) => (
        <ul style={{ paddingLeft: 18, margin: 0 }}>
            {(list || []).map((c) => (
                <li key={c.id}>
                    {/* 27.10 düzeltmesi: aynı fiziksel kabinin eski bsk'sine bağlı fatura ('kabin' eşleşmesi) ayrıca belirtilir */}
                    {`${c.evrakNo || '(evraksız)'} · ${formatDate(c.donemBaslangic)} - ${formatDate(c.donemBitis)} · ${formatMoney(c.fatura_tutar)} · ${c.durum}${c.fatura_turu_ad ? ` · ${c.fatura_turu_ad}` : ''}${c.eslesme === 'kabin' && c.bsk ? ` · önceki söz. bsk #${c.bsk}` : ''} (#${c.id})`}
                </li>
            ))}
        </ul>
    );

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const mParam = urlParams.get('m') ? parseInt(urlParams.get('m'), 10) : new Date().getMonth() + 1;
        const yParam = urlParams.get('y') ? parseInt(urlParams.get('y'), 10) : new Date().getFullYear();

        setFilters({
            month: mParam,
            year: yParam
        });

        if (id) {
            loadData(id, mParam, yParam);
        } else {
            setError('ID parametresi bulunamadı. Lütfen geçerli bir kayıt seçin.');
        }
    }, [id]);

    const loadData = async (id, m, y) => {
        setLoading(true);
        setError(null);

        try {
            const requestData = {
                params: {
                    id: id
                }
            };

            const response = await fetch(`${API_BASE_URL}/list/execute-query/banka-sozlesme-kayit-detay`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            console.log('API Response:', result);
            console.log('Response type:', typeof result);
            const lokasyonAdi = result.Lokasyon;
            console.log('🏢 Lokasyon Adı:', lokasyonAdi);    
            
            if (!result || (typeof result === 'object' && Object.keys(result).length === 0)) {
                throw new Error(`Boş veri döndü. ID: ${id} için kayıt bulunamadı.`);
            }
                        
            const cabinBankName = result.Banka || result.banka_adi || result.bank_name || result.banka;

            // 26.10 düzeltmesi (F3/F6 banka): tutar ve dönem artık yerelde (2019_kira, bugünün enflasyonu,
            // kabin_kira_gelirleri + bsk.banka) hesaplanmıyor; /contracts/bank-invoice/calc aynı kuralı
            // BankaFaturaTab ile paylaşır: hedef yıl/ay, artış ayından bir önceki ayın endeksi, baz = son fatura.
            const calcRes = await fetch(`${API_BASE_URL}/contracts/bank-invoice/calc/${id}?yil=${y || new Date().getFullYear()}&ay=${m || new Date().getMonth() + 1}`);
            if (!calcRes.ok) {
                const t = await calcRes.text();
                throw new Error(`Hesap alınamadı (${calcRes.status}) - ${t}`);
            }
            const calcJson = await calcRes.json();
            const calc = calcJson?.data || {};
            setInflationData(calc);

            const bankCodeReq = {
                params: {
                    banka: result.banka_no,
                    lokasyon: result['Lokasyon No'] || result.lokasyonNo,
                    kabin: result['Kabin No'] || result.kabinNo
                }
            };
            const bankCodeRes = await fetch(`${API_BASE_URL}/list/execute-query/banka-kodu`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bankCodeReq)
            });
            const bankCodeData = bankCodeRes.ok ? await bankCodeRes.json() : {};
            const fetchedBankCode = bankCodeData?.bankaKodu || result.banka_kabin_no || '';
            setBankCode(fetchedBankCode);

            const discountReq = {
                params: {
                    lokasyon: result.lokasyon || result.lokasyonNo,
                    banka: result.banka_no,
                    tipi: 4,
                    tarih: dayjs().subtract(1, 'year').format('YYYY-MM-DD')
                }
            };
            const discountRes = await fetch(`${API_BASE_URL}/list/execute-query/banka-indirim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(discountReq)
            });
            const discountData = discountRes.ok ? await discountRes.json() : [];
            setDiscountOptions(Array.isArray(discountData) ? discountData : []);

            const sozlesmeReq = {
                params: {
                    id: result.sozlesme
                }
            };
            const sozlesmeRes = await fetch(`${API_BASE_URL}/list/execute-query/sozlesme-detay`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sozlesmeReq)
            });
            const sozlesmeData = sozlesmeRes.ok ? await sozlesmeRes.json() : null;
            console.log('Sozlesme response:', sozlesmeData);

            const startDate = calc.donem_baslangic ? dayjs(calc.donem_baslangic) : null;
            const endDate = calc.donem_bitis ? dayjs(calc.donem_bitis) : null;
            const invoiceAmount = Number(calc.fatura_tutar || 0);
            setCalculatedAmount(invoiceAmount);

            const ysSozlesmeReq = {
                params: {
                    lokasyon: result.lokasyon || result.lokasyonNo
                }
            };
            const ysSozlesmeRes = await fetch(`${API_BASE_URL}/list/execute-query/ys-sozlesme-detay`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ysSozlesmeReq)
            });
            const ysSozlesmeData = ysSozlesmeRes.ok ? await ysSozlesmeRes.json() : null;

            const nextData = {
                ...result,
                ...sozlesmeData,
                ys_sozlesme: ysSozlesmeData,
                calc: calc,
                calculatedIncreaseRate: calc.artis_orani || 0,
                calculatedRent: calc.hesaplanan_kira || 0,
                faturaSuresi: calc.gun
            };
            setData(nextData);
            setContractData(sozlesmeData);

            form.setFieldsValue({
                donemBaslangic: startDate && startDate.isValid() ? startDate : null,
                donemBitis: endDate && endDate.isValid() ? endDate : null,
                faturaTuru: calc.onerilen_fatura_turu || 3,
                firma: calc.firma || sozlesmeData?.firma || 1,
                fatura_tutar: invoiceAmount || 0,
                bankaKodu: fetchedBankCode,
                indirim: 0 
            });

        } catch (err) {
            console.error('Data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => formatMoney(amount);

    const formatDate = (date) => {
        return dayjs(date).format('DD.MM.YYYY');
    };

    const calculateDateDifference = (startDate, endDate) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diff = end.diff(start, 'day');
        
        const years = Math.floor(diff / 365);
        const months = Math.floor((diff % 365) / 30);
        const days = diff % 30;
        
        let result = '';
        if (years > 0) result += `${years} yıl `;
        if (months > 0) result += `${months} ay `;
        if (days > 0) result += `${days} gün`;
        
        return result.trim();
    };

    // 26.10 düzeltmesi (F6 banka): eskiden /form/submit-form'a tireli tablo adıyla ('banka-fatura-talebi')
    // 'Update' + id=bsk.id gönderiliyordu: her seferinde 400 dönüyordu, tablo adı düzeltilse de alakasız bir
    // faturanın üstüne yazacaktı. Talep artık /contracts/bank-invoice/request ile INSERT edilir (lokasyon,
    // kabin, banka bsk'den çözülür; mükerrer dönemde 409 + çakışanlar, yalnız açık onayla force).
    // 27.10 düzeltmesi: 409'lar kodlu (mukerrer / imza / hesap = endeks yayımlanmamış ya da zayıf baz); her
    // onaylanan kod 'onaylar'a eklenip yeniden gönderilir.
    const onayAciklama = {
        mukerrer: "Aynı bsk'ye bağlı ikinci bir gerçek kabin faturası gibi bilinçli bir durum değilse vazgeçin.",
        imza: 'Kapanış / kalan dönem faturası gibi bilinçli bir durum değilse vazgeçin.',
        hesap: 'Endeks yayımlanınca yeniden hesaplayın; tutarı elle kontrol edip düzelttiyseniz onaylayın.'
    };
    const hesapSorunlari = (c) => {
        const out = [];
        const eksik = (c && c.endeks_eksik) || [];
        if (eksik.length) out.push(`Endeks yayımlanmamış: ${eksik.join(', ')} — bu yılların artışı uygulanmadı (%0).`);
        if (c && c.baz_zayif) out.push('Fatura geçmişi yok; baz sözleşme kira bedeli üzerinden çok yıllık bileşik artış. Tutarı elle kontrol edin.');
        return out;
    };
    const hesapUyariRe = /örtüşen|Endeks yayımlanmamış|Baz sözleşme kira bedeli x/;

    const handleSubmit = async (values, onaylar = []) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/bank-invoice/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bsk_id: Number(id),
                    firma: values.firma,
                    donem_baslangic: values.donemBaslangic.format('YYYY-MM-DD'),
                    donem_bitis: values.donemBitis.format('YYYY-MM-DD'),
                    fatura_tutar: Number(values.fatura_tutar || calculatedAmount || 0),
                    fatura_turu: values.faturaTuru,
                    banka_kodu: values.bankaKodu || '',
                    indirim: values.indirim || 0,
                    ...(onaylar.length ? { onaylar } : {})
                })
            });
            const body = await response.json().catch(() => ({}));
            const det = body?.detail;
            const kod = response.status === 409 && det && typeof det === 'object'
                ? (det.kod || (det.cakisanlar ? 'mukerrer' : null)) : null;
            if (kod && !onaylar.includes(kod)) {
                Modal.confirm({
                    title: det.message || 'Onay gerekli',
                    width: 640,
                    content: (
                        <div>
                            {kod === 'mukerrer' && (
                                <>
                                    <p>Örtüşen geçerli kayıtlar:</p>
                                    {cakismaListesi(det.cakisanlar)}
                                </>
                            )}
                            {kod === 'hesap' && (
                                <>
                                    <ul style={{ paddingLeft: 18, margin: 0 }}>{(det.sorunlar || []).map((x) => <li key={x}>{x}</li>)}</ul>
                                    <p style={{ marginTop: 8 }}>{`Önerilen ${formatMoney(det.onerilen_tutar)} · gönderilen ${formatMoney(det.gonderilen_tutar)}`}</p>
                                </>
                            )}
                            <p style={{ marginTop: 8 }}>{onayAciklama[kod] || ''}</p>
                        </div>
                    ),
                    okText: 'Yine de oluştur',
                    okButtonProps: { danger: true },
                    cancelText: 'Vazgeç',
                    onOk: () => handleSubmit(values, [...onaylar, kod])
                });
                return;
            }
            if (!response.ok) {
                const det = body?.detail;
                throw new Error(typeof det === 'string' ? det : (det?.message || `HTTP ${response.status}`));
            }
            message.success(`Banka fatura talebi oluşturuldu (#${body?.data?.id})`);
            loadData(id, filters.month, filters.year);
        } catch (err) {
            console.error('Submit error:', err);
            message.error('Kaydetme sırasında hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !data) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '50vh',
                background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)'
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert
                    message="Hata"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert
                    message="Veri Bulunamadı"
                    description="Banka fatura talebi için gerekli veriler yüklenemedi."
                    type="warning"
                    showIcon
                />
            </div>
        );
    }
    console.log("Data:", data);

    let extractedData = data;
    
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const keys = Object.keys(data);
        console.log("Keys:", keys);
        if (keys.length === 1 && typeof data[keys[0]] === 'object') {
            extractedData = data[keys[0]];
            console.log("Extracted Data:", extractedData.Lokasyon);
        }
    }
    const kabinNo = extractedData?.kabinNo || extractedData?.['Kabin No'] || 'Bilinmiyor';
    let lokasyonAdi = 'Bilinmiyor'; 
    const extractLocationName = (lokasyonValue) => {
        if (!lokasyonValue) return null;
        let lokasyonObj = lokasyonValue;
        if (typeof lokasyonValue === 'string') {
            try {
                lokasyonObj = JSON.parse(lokasyonValue);
            } catch (e) {
                return lokasyonValue;
            }
        }
        
        if (typeof lokasyonObj === 'object' && lokasyonObj !== null) {
            const firstKey = Object.keys(lokasyonObj)[0];
            if (firstKey && lokasyonObj[firstKey]?.ad) {
                return lokasyonObj[firstKey].ad;
            }
        }
        
        return null;
    };
    
    lokasyonAdi = extractLocationName(extractedData?.Lokasyon) || 
                 extractLocationName(data?.Lokasyon) || 
                 extractedData?.lokasyonAdi || 
                 extractedData?.ad || 
                 'Bilinmiyor';
    
    console.log('🏢 Render Lokasyon Adı:', lokasyonAdi);
    let bankaAdi = 'Bilinmiyor';
    if (extractedData?.Banka) {
        bankaAdi = extractedData.Banka;
    }
    else if (data?.Banka) {
        bankaAdi = data.Banka;
    }
    else if (extractedData?.banka_adi || data?.banka_adi) {
        bankaAdi = extractedData?.banka_adi || data?.banka_adi;
    }
    else if (extractedData?.banka || data?.banka) {
        bankaAdi = extractedData?.banka || data?.banka;
    }
    
    return (
        <div className={`banka-fatura-page ${darkMode ? 'dark-theme' : ''}`} 
             style={{ 
                 padding: '20px',
                 background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)',
                 minHeight: '100vh'
             }}>
            <Card 
                className={darkMode ? 'dark-card' : ''}
                style={{
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Title level={2} style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                    {`${lokasyonAdi} | ${kabinNo}. Kabin ${bankaAdi} | ${monthNames[(filters.month || new Date().getMonth()) - 1]} Ayı Fatura Talebi`}
                </Title>
                
                <Divider />

                {(data?.calc?.mevcut_faturalar || []).length > 0 && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }}
                        message="Bu dönemle örtüşen fatura/talep var"
                        description={cakismaListesi(data.calc.mevcut_faturalar)} />
                )}
                {hesapSorunlari(data?.calc).length > 0 && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }}
                        message="Hesap eksik veriyle yapıldı; talep açık onay ister"
                        description={<ul style={{ paddingLeft: 18, margin: 0 }}>{hesapSorunlari(data.calc).map((u) => <li key={u}>{u}</li>)}</ul>} />
                )}
                {(data?.calc?.uyarilar || []).filter((u) => !hesapUyariRe.test(u)).length > 0 && (
                    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                        message={<ul style={{ paddingLeft: 18, margin: 0 }}>{data.calc.uyarilar.filter((u) => !hesapUyariRe.test(u)).map((u) => <li key={u}>{u}</li>)}</ul>} />
                )}

                <Row gutter={[24, 24]}>
                    {/* Information Table */}
                    <Col xs={24} lg={10}>
                        <Card 
                            title="Lokasyon Bilgileri"
                            size="small"
                            className={darkMode ? 'dark-card' : ''}
                            style={{
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                                borderColor: darkMode ? 'var(--dark-border)' : undefined
                            }}
                        >
                            <Table
                                rowKey="key"
                                columns={[
                                    { title: '', dataIndex: 'key', key: 'k' },
                                    { title: '', dataIndex: 'value', key: 'v' }
                                ]}
                                dataSource={[
                                    { key: 'İl / İlçe', value: `${extractedData?.İl || data?.İl || 'Bilinmiyor'} / ${extractedData?.İlçe || data?.İlçe || 'Bilinmiyor'}` },
                                    { key: 'Lokasyon Durumu', value: locationStatusOptions[extractedData?.durum || data?.durum] || locationStatusOptions[data?.lokasyon_durum] || 'Bilinmiyor' },
                                    { key: 'Sözleşme Kira Bedeli', value: formatCurrency(extractedData?.kiraBedeli || data?.kiraBedeli || 0) },
                                    { key: 'Baz (yıllık) / Kaynağı',
                                      value: `${formatCurrency(data?.calc?.gecen_yil_kira || 0)} / ${bazKaynagiLabel(data?.calc?.baz_kaynagi)}`
                                    },
                                    { key: 'Önceki Fatura',
                                      value: data?.calc?.onceki_fatura
                                          ? `${data.calc.onceki_fatura.evrakNo || '(evraksız)'} · ${formatDate(data.calc.onceki_fatura.donemBaslangic)} - ${formatDate(data.calc.onceki_fatura.donemBitis)} · ${formatCurrency(data.calc.onceki_tutar)}`
                                          : '-'
                                    },
                                    { key: 'Artış Oranı / Şekli',
                                      value: `${increaseRateOptions[data?.artisOrani] || 'Bilinmiyor'} / ${increaseMethodOptions[data?.artisSekli] || 'Bilinmiyor'} (%${data?.calculatedIncreaseRate || 0})`
                                    },
                                    { key: 'Artış Ayı / Endeks Ayı',
                                      value: `${data?.calc?.artis_ayi || '-'}${data?.calc?.artis_ayi_turetilmis ? ' (türetilmiş)' : ''} / ${data?.calc?.endeks_ay || '-'}`
                                    },
                                    { key: 'Hesaplanan Yıllık Kira', value: formatCurrency(data?.calculatedRent || 0) },
                                    { key: 'Sonraki Artış Tarihi', value: data?.calc?.sonraki_artis_tarihi ? formatDate(data.calc.sonraki_artis_tarihi) : '-' },
                                    { key: 'Kira Başlangıç Tarihi', value: extractedData?.kiraBaslangic || data?.kiraBaslangic ? formatDate(extractedData?.kiraBaslangic || data?.kiraBaslangic) : 'Bilinmiyor' },
                                    ...(data?.sozlesmeBaslangic && data?.sozlesmeBitis ? [{
                                        key: 'Sözleşme Başlangıç / Bitiş Tarihi | İmza Durumu',
                                        value: `${formatDate(data.sozlesmeBaslangic)} / ${formatDate(data.sozlesmeBitis)} (${calculateDateDifference(data.sozlesmeBaslangic, data.sozlesmeBitis)}) | ${contractSignatureStatusOptions[data.sozlesmeImzaDurumu] || 'Bilinmiyor'}`
                                    }] : []),
                                    ...(data?.ys_sozlesme?.bitis ? [{
                                        key: 'Ys. Sözleşme Bitiş Tarihi | İmza Durumu',
                                        value: `${formatDate(data.ys_sozlesme.bitis)} | ${contractSignatureStatusOptions[data.ys_sozlesme.sozlesmeImzaDurumu] || 'Bilinmiyor'}`
                                    }] : []),
                                    { key: 'Temlik Durumu', value: contractData?.temlik ? 'Var' : 'Yok' }
                                ]}
                                pagination={false}
                                showHeader={false}
                                size="small"
                                className={darkMode ? 'dark-table' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                            />
                        </Card>
                    </Col>

                    {/* Form */}
                    <Col xs={24} lg={14}>
                        <Card 
                            title="Fatura Bilgileri"
                            size="small"
                            className={darkMode ? 'dark-card' : ''}
                            style={{
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                                borderColor: darkMode ? 'var(--dark-border)' : undefined
                            }}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={(values) => handleSubmit(values)}
                                className={darkMode ? 'dark-form' : ''}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Dönemi Başlangıç"
                                            name="donemBaslangic"
                                            rules={[{ required: true, message: 'Başlangıç tarihi gerekli' }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                className={darkMode ? 'dark-date-picker' : ''}
                                                placeholder="Başlangıç tarihi seçin"
                                                format="DD-MM-YYYY"
                                            />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Dönemi Bitiş"
                                            name="donemBitis"
                                            rules={[{ required: true, message: 'Bitiş tarihi gerekli' }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                className={darkMode ? 'dark-date-picker' : ''}
                                                placeholder="Bitiş tarihi seçin"
                                                format="DD-MM-YYYY"                                            
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Fatura Tutarı (KDV Hariç)"
                                    name="fatura_tutar"
                                    rules={[{ required: true, message: 'Fatura tutarı gerekli' }]}
                                >
                                    <Input
                                        type="number"
                                        step="any"
                                        placeholder="Fatura tutarı"
                                        className={darkMode ? 'dark-input' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Banka Kodu"
                                    name="bankaKodu"
                                >
                                    <Input
                                        placeholder="Banka Kodu"
                                        className={darkMode ? 'dark-input' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    />
                                </Form.Item>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Türü"
                                            name="faturaTuru"
                                            rules={[{ required: true, message: 'Fatura türü gerekli' }]}
                                        >
                                            <Select
                                                className={darkMode ? 'dark-select' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                {invoiceTypeOptions.map(option => (
                                                    <Option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="İndirim"
                                            name="indirim"
                                        >
                                            <Select
                                                className={darkMode ? 'dark-select' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                {discountOptions.map(option => (
                                                    <Option key={option.id} value={option.id}>
                                                        {`${option.teklif} (${formatDate(option.tarih)}) - ${option.ekleyen_name || option.ekleyen}`}
                                                    </Option>
                                                ))}
                                                <Option value={0}>Yok</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Firma"
                                    name="firma"
                                    rules={[{ required: true, message: 'Firma seçimi gerekli' }]}
                                >
                                    <Select
                                        className={darkMode ? 'dark-select' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        {companyOptions.map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item>
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            loading={loading}
                                            danger={hesapSorunlari(data?.calc).length > 0 || (data?.calc?.mevcut_faturalar || []).length > 0}
                                            className={darkMode ? 'dark-button' : ''}
                                            style={{
                                                backgroundColor: darkMode ? 'var(--dark-primary)' : undefined,
                                                borderColor: darkMode ? 'var(--dark-primary)' : undefined
                                            }}
                                        >
                                            Kaydet
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default BankaFaturaTalepIslem;
